import GameSignals from '@/signals/GameSignals'
import Facade from '@/Facade'
import { toMoney } from '@base/utils/Formatter'
import { Utilities } from '@base/utils/Utilities'
import { UIAPI } from '@modules/src/managers/UIAPI'
import { UIGameEvent, UIEvent } from '@modules/src/enums/UIEvents'
import { TranslationUtils } from '@modules/src/utils/TranslationUtils'
import { Utils } from '@modules/src/utils/Utils'
import { ModulesManager } from '@modules/src/managers/ModulesManager'
import { TranslationsModule } from '@modules/src/modules/TranslationsModule'
import fastPlayPromptManager from '@modules/src/managers/FastPlayPromptManager'
import gsap from 'gsap'
import { PopupContent } from '@/modules'
import { Model } from '@/model/Model'
import { RoundRestoreUtil } from '@/utils/RoundRestoreUtil'
import { isFeatureRoundActive, isSwipeBlockedByGameState } from '@/utils/SwipeGating'

const FAST_PLAY_LEGACY_STYLE = {
    title: {
        fill: ['#ffc000', '#fff600', '#ffc000'],
        stroke: '#813c17',
        strokeThickness: 10,
        fontFamily: 'Montserrat',
    },
    text: {
        fill: ['#ffc000', '#fff600', '#ffc000'],
        stroke: '#813c17',
        strokeThickness: 5,
        fontFamily: 'Montserrat',
    },
    button: {
        fill: ['#fff600', '#fff600'],
        fontFamily: 'Montserrat',
    },
}

const ERROR_CODE_TRANSLATION_KEYS: Readonly<Record<string, string>> = {
    INVALID_USERNAME: 'Username not found',
    USERNAME_NOT_FOUND: 'Username not found',
    INSUF_BALL_001: 'You have insufficient balance!',
    INSUFFICIENT_BALANCE: 'You have insufficient balance!',
    INSUFFICIENT_FUNDS: 'You have insufficient balance!',
    SESSION_TIMEOUT: 'Session Time Out. Please reload the game.',
    STIMEOUT: 'Session Time Out. Please reload the game.',
    INVALID_SESSION: 'Invalid session or multisession not allowed',
    MULTISESSION: 'Invalid session or multisession not allowed',
    MULTISESSION_NOT_ALLOWED: 'Invalid session or multisession not allowed',
    MULTI_SESSION: 'Invalid session or multisession not allowed',
    MULTI_SESSION_NOT_ALLOWED: 'Invalid session or multisession not allowed',
    INVALID_SESSION_OR_MULTISESSION_NOT_ALLOWED: 'Invalid session or multisession not allowed',
    GAME_UNAVAILABLE: 'Game Unavailable.',
    INVALID_COINS: 'Invalid number of coins.',
    INVALID_NUMBER_OF_COINS: 'Invalid number of coins.',
    INVALID_BETLINES: 'Invalid betlines.',
    WRONG_PAYLINE_CONFIGURATION: 'Wrong payline configuration. Please reload game.',
    WRONG_PAYLINES: 'Wrong paylines. Please reload game.',
}

const ERROR_MESSAGE_TRANSLATION_KEYS: Readonly<Record<string, string>> = {
    'username not found': 'Username not found',
    'you have insufficient balance': 'You have insufficient balance!',
    'insufficient balance': 'You have insufficient balance!',
    'insufficient funds': 'You have insufficient balance!',
    'invalid session or multisession not allowed': 'Invalid session or multisession not allowed',
    'session time out please reload the game': 'Session Time Out. Please reload the game.',
    'game unavailable': 'Game Unavailable.',
    'invalid number of coins': 'Invalid number of coins.',
    'invalid betlines': 'Invalid betlines.',
    'wrong payline configuration please reload game': 'Wrong payline configuration. Please reload game.',
    'wrong paylines please reload game': 'Wrong paylines. Please reload game.',
}

export default class PopupManager {
    private static readonly FAST_PLAY_DEBUG = true
    private _enabled: boolean = true
    private _currentType: string
    private _isFastPlayPopupRequestPending: boolean = false
    private _isFastPlayPopupFlushScheduled: boolean = false
    private _model: Model
    private _autoRestoreConfirmTimeoutId: number = null
    private _promoFreeSpinsPopupShown: boolean = false
    private _translationsReady: Promise<void> | null = null
    // The `public/ml/*.js` dictionaries are keyed by the English copy, so each string below is both
    // the lookup key and the English fallback. camelCase ids never matched any dictionary entry.
    private static readonly CONNECTION_ERROR_TITLE = 'Message'
    private static readonly CONNECTION_ERROR_TEXT = 'Server has been disconnected. Please reload the game.'
    private static readonly NO_INTERNET_TEXT = 'No active internet! Please reload game.'
    private static readonly CONNECTION_ERROR_BUTTON = 'Reload'
    private static readonly CASINO_ERROR_TITLE = 'Message'
    private static readonly CASINO_ERROR_TEXT = 'Server has been disconnected. Please reload the game.'
    private static readonly CASINO_ERROR_BUTTON = 'Reload'
    private static readonly FAST_PLAY_TITLE = 'Fast Play'
    private static readonly FAST_PLAY_TEXT = 'Would you like to enable Fast Play mode?'
    private static readonly FAST_PLAY_ENABLE = 'YES'
    private static readonly FAST_PLAY_DISABLE = 'NO'
    private static readonly INSUFFICIENT_BALANCE_CODE = 'INSUF_BALL_001'
    private static readonly PROMO_FREE_SPINS_CODES = [
        'PROMO_FREE_SPINS',
        'PROMO_FREE_SPINS_UNFINISHED',
        'PROMO_FREE_SPINS_ENDED',
        'PROMO_FREE_SPINS_MAX_WIN_REACHED',
        'PROMO_FREE_SPINS_REACHED',
    ]
    public constructor(model: Model) {
        GameSignals.enableGamePopups.connect(this.enable.bind(this))
        GameSignals.disableGamePopups.connect(this.disable.bind(this))
        GameSignals.requestSwipeFeaturePopup.connect(this.onRequestSwipeFeaturePopup)
        ;(GameSignals as any).requestFastPlayPopup?.connect(this.onRequestFastPlayPopup)
        ;(GameSignals as any).featureIntroRequested?.connect(this.onFeatureInterruptClearFastPlay)
        ;(GameSignals as any).holdAndWinTransitionStart?.connect(this.onFeatureInterruptClearFastPlay)
        ;(GameSignals as any).holdAndWinStartSpin?.connect(this.onFeatureInterruptClearFastPlay)
        ;(GameSignals as any).holdAndWinStop?.connect(this.onFeatureInterruptClearFastPlay)
        GameSignals.sessionTimeout.connect(this.onSessionTimeout)
        GameSignals.realityCheck.connect(this.onRealityCheckPopup)
        GameSignals.realityCheck2.connect(this.onRealityCheckPopup2)
        GameSignals.casinoError.connect(this.onCasinoError)
        GameSignals.errorConnection.connect(this.onErrorServer)
        GameSignals.previousRound.connect(this.onPreviousRoundPopup)
        GameSignals.previousRoundActive.connect(this.onPreviousRoundPopupActive)
        ;(GameSignals as any).unfinishedRoundContinue?.connect(this.onUnfinishedRoundContinue)
        GameSignals.hardwareAcceleration.connect(this.onHardwareAcceleration)
        GameSignals.commonError.connect(this.onCommonError)

        this._currentType = null
        this._model = model
    }

    private enable() {
        this._enabled = true
        this.scheduleFastPlayPopupFlush()
    }

    private disable() {
        this._enabled = false
        this.clearPendingFastPlayPopupRequest('popups-disabled')
    }

    /** Drop any queued Fast Play so it cannot appear after FS / H&W. */
    private onFeatureInterruptClearFastPlay = () => {
        this.clearPendingFastPlayPopupRequest('feature-interrupt')
        ;(fastPlayPromptManager as any).discardPendingPrompt?.('feature-interrupt')
    }

    private clearPendingFastPlayPopupRequest(reason: string): void {
        if (!this._isFastPlayPopupRequestPending) {
            return
        }

        if (PopupManager.FAST_PLAY_DEBUG) {
            console.log('[FastPlayPopup] dropped pending request:', reason)
        }

        this._isFastPlayPopupRequestPending = false
        ;(GameSignals as any).fastPlayPopupRejected?.emit(reason)
    }

    onActionButtonClick(action: string, closePopup: () => void) {
        switch (action) {
            case 'reload':
                this.onReloadGamePageClick()
                break
            case 'quit':
            case 'lobby':
                this.onQuitClick()
                break
            default:
                this.onResumeGameClick(closePopup)
                break
        }
    }

    private onRequestSwipeFeaturePopup = () => {
        if (this._currentType != null || !this._enabled) {
            return
        }

        // Buy Bonus (and similar) set this without using PopupManager._currentType.
        if (Facade.isPopupBlockingInput) {
            return
        }

        // Offering the feature only makes sense where the gesture would actually spin: not on the
        // preloader / intro, not inside Free Game or Hold & Win, and not during their transitions.
        if (isFeatureRoundActive() || isSwipeBlockedByGameState()) {
            return
        }

        const config =
            typeof Facade.defaultConfig?.mobileSwipeSpin === 'object' ? Facade.defaultConfig.mobileSwipeSpin : undefined
        const popupConfig = config?.popup

        this.showPopup('swipeFeature', {
            variant: 'swipe-feature',
            title: this.resolveTranslation(popupConfig?.title ?? 'Swipe Feature'),
            text: this.resolveTranslation(popupConfig?.text ?? 'Swipe Down to Spin the Reels'),
            buttons: [
                {
                    label: this.resolveTranslation(popupConfig?.enableLabel ?? 'ENABLE'),
                    callback: this.onEnableSwipeFeature,
                },
                {
                    label: this.resolveTranslation(popupConfig?.disableLabel ?? 'RESUME GAME'),
                    callback: this.onDisableSwipeFeature,
                },
            ],
        })

        if (this._currentType === 'swipeFeature') {
            GameSignals.swipeFeaturePopupShown.emit()
        }
    }

    private onEnableSwipeFeature = (closePopup: () => void) => {
        this.hidePopup()
        closePopup()
        // Enable only — do not spin until the player swipes.
        GameSignals.enableSwipeFeature.emit(true)
    }

    private onRequestFastPlayPopup = () => {
        if (!this._enabled) {
            if (PopupManager.FAST_PLAY_DEBUG) {
                console.log('[FastPlayPopup] blocked: popups disabled')
            }
            ;(GameSignals as any).fastPlayPopupRejected?.emit('popups-disabled')
            return
        }

        // Buy Bonus (and similar canvas overlays) block input without PopupManager._currentType.
        if (Facade.isPopupBlockingInput) {
            if (PopupManager.FAST_PLAY_DEBUG) {
                console.log('[FastPlayPopup] blocked: another popup is blocking input')
            }
            this.clearPendingFastPlayPopupRequest('popup-blocking')
            return
        }

        if (this._currentType != null) {
            if (PopupManager.FAST_PLAY_DEBUG) {
                console.log('[FastPlayPopup] queued: popup manager busy', { currentType: this._currentType })
            }
            this._isFastPlayPopupRequestPending = true
            this.scheduleFastPlayPopupFlush()
            return
        }

        const hasShown = this.showFastPlayPopup()
        if (!hasShown) {
            this._isFastPlayPopupRequestPending = true
            this.scheduleFastPlayPopupFlush()
        }
    }

    private showFastPlayPopup(): boolean {
        if (PopupManager.FAST_PLAY_DEBUG) {
            console.log('[FastPlayPopup] showing Fast Play popup')
        }

        this.showPopup('fastPlayFeature', {
            variant: 'fast-play-feature',
            title: this.resolveTranslation(PopupManager.FAST_PLAY_TITLE),
            text: this.resolveTranslation(PopupManager.FAST_PLAY_TEXT),
            //      style: FAST_PLAY_LEGACY_STYLE,
            buttons: [
                {
                    label: this.resolveTranslation(PopupManager.FAST_PLAY_ENABLE),
                    callback: this.onEnableFastPlay,
                },
                {
                    label: this.resolveTranslation(PopupManager.FAST_PLAY_DISABLE),
                    callback: this.onDisableFastPlay,
                },
            ],
        })

        if (this._currentType === 'fastPlayFeature') {
            this._isFastPlayPopupRequestPending = false
            ;(GameSignals as any).fastPlayPopupDisplayed?.emit()
            return true
        } else {
            if (PopupManager.FAST_PLAY_DEBUG) {
                console.log('[FastPlayPopup] postponed: could not display now', {
                    currentType: this._currentType,
                })
            }
            return false
        }
    }

    private scheduleFastPlayPopupFlush(): void {
        if (this._isFastPlayPopupFlushScheduled) {
            return
        }

        this._isFastPlayPopupFlushScheduled = true
        setTimeout(() => {
            this._isFastPlayPopupFlushScheduled = false
            this.tryFlushFastPlayPopupRequest()
        }, 0)
    }

    private tryFlushFastPlayPopupRequest(): void {
        if (!this._isFastPlayPopupRequestPending) {
            return
        }

        // The prompt is a once-per-session offer. A request queued behind another popup must not
        // outlive the display it was queued for, or the player is asked a second time.
        if ((fastPlayPromptManager as any).getState?.().hasShown) {
            this.clearPendingFastPlayPopupRequest('already-shown')
            return
        }

        if (!this._enabled) {
            if (PopupManager.FAST_PLAY_DEBUG) {
                console.log('[FastPlayPopup] dropped pending request: popups disabled')
            }
            this._isFastPlayPopupRequestPending = false
            ;(GameSignals as any).fastPlayPopupRejected?.emit('popups-disabled')
            return
        }

        if (this._currentType != null) {
            return
        }

        const hasShown = this.showFastPlayPopup()
        if (!hasShown) {
            this.scheduleFastPlayPopupFlush()
        }
    }

    private onEnableFastPlay = (closePopup: () => void) => {
        this.hidePopup()
        closePopup()
        GameSignals.toggleTurbo.emit(true)
    }

    private onDisableFastPlay = (closePopup: () => void) => {
        this.hidePopup()
        closePopup()
    }

    private onDisableSwipeFeature = (closePopup: () => void) => {
        this.hidePopup()
        closePopup()
        GameSignals.enableSwipeFeature.emit(false)
    }

    private onErrorServer = async () => {
        await this.ensureTranslationsLoaded()

        const sourceText = this.isOffline() ? PopupManager.NO_INTERNET_TEXT : PopupManager.CONNECTION_ERROR_TEXT

        this.showPopup('errorServer', {
            title: this.resolveTranslation(PopupManager.CONNECTION_ERROR_TITLE),
            text: this.resolveTranslation(sourceText),
            buttons: [
                {
                    label: this.resolveTranslation(PopupManager.CONNECTION_ERROR_BUTTON),
                    callback: this.onReloadGamePageClick,
                },
            ],
        })
    }

    private onCasinoError = async (data: any) => {
        console.log('[PopupManager] onCasinoError', {
            status: data?.status,
            errorCode: data?.error_code ?? data?.error?.code ?? data?.code ?? data?.error?.name,
            currentType: this._currentType,
            promoPopupShown: this._promoFreeSpinsPopupShown,
        })

        if (this.isInsufficientBalanceError(data)) {
            await this.showInsufficientBalancePopup()
            return
        }

        if (this.isPromoFreeSpinsError(data)) {
            console.log('[PopupManager] promoFreeSpins candidate', {
                promoPopupShown: this._promoFreeSpinsPopupShown,
                currentType: this._currentType,
                promoCode: data?.error_code ?? data?.error?.code ?? data?.code ?? data?.error?.name,
            })

            const promoCode = String(
                data?.error_code ?? data?.error?.code ?? data?.code ?? data?.error?.name ?? ''
            ).toUpperCase()
            const isTerminalPromo =
                promoCode === 'PROMO_FREE_SPINS_ENDED' ||
                promoCode === 'PROMO_FREE_SPINS_MAX_WIN_REACHED' ||
                promoCode === 'PROMO_FREE_SPINS_REACHED'

            if (this._promoFreeSpinsPopupShown && !isTerminalPromo) {
                console.log('[PopupManager] promoFreeSpins skipped because popup already handled')
                return
            }

            this._promoFreeSpinsPopupShown = true
            console.log('[PopupManager] promoFreeSpins emitted', {
                promoPopupShown: this._promoFreeSpinsPopupShown,
                currentType: this._currentType,
            })
            ;(GameSignals as any).promoFreeSpins.emit(data)
            return
        }

        await this.ensureTranslationsLoaded()

        const statusCode = Number(data?.status)
        const title = this.resolveTranslation(PopupManager.CASINO_ERROR_TITLE)
        const text = this.resolveCasinoErrorText(data, statusCode)

        this.showPopup('casinoError', {
            title,
            text,
            buttons: [
                {
                    label: this.resolveTranslation(PopupManager.CASINO_ERROR_BUTTON),
                    callback: this.onReloadGamePageClick,
                },
            ],
        })
    }

    /**
     * Keep this popup in the game popup pipeline: SharedPopupManager uses the raw RGS/config copy,
     * which bypasses the game's `ml` dictionary. `casinoError` has already stopped the rejected spin,
     * so a second `insufficientBalance` signal is not required for reel/state recovery.
     */
    private async showInsufficientBalancePopup(): Promise<void> {
        await this.ensureTranslationsLoaded()

        const config =
            typeof Facade.defaultConfig?.insufficientBalancePopup === 'object'
                ? Facade.defaultConfig.insufficientBalancePopup
                : undefined
        const depositUrl = String(
            (Facade.urlParams as any)?.deposit_url || (Facade.urlParams as any)?.depositUrl || ''
        ).trim()
        const buttons: PopupContent['buttons'] = []

        if (config?.deposit && /^https?:\/\//i.test(depositUrl)) {
            buttons.push({
                label: this.resolveTranslation(config.deposit.buttonText || 'DEPOSIT'),
                callback: (closePopup: () => void) => {
                    this.onResumeGameClick(closePopup)
                    window.location.assign(depositUrl)
                },
            })
        }

        buttons.push({
            label: this.resolveTranslation(config?.continue?.buttonText || 'CONTINUE'),
            callback: this.onResumeGameClick,
        })

        this.showPopup('insufficientBalance', {
            variant: 'insufficient-balance',
            title: this.resolveTranslation(PopupManager.CASINO_ERROR_TITLE),
            text: this.resolveTranslation('You have insufficient balance!'),
            buttons,
        })
    }

    private onCommonError = async () => {
        await this.ensureTranslationsLoaded()

        const sourceText = this.isOffline() ? PopupManager.NO_INTERNET_TEXT : PopupManager.CASINO_ERROR_TEXT

        this.showPopup('commonError', {
            title: this.resolveTranslation(PopupManager.CASINO_ERROR_TITLE),
            text: this.resolveTranslation(sourceText),
            buttons: [
                {
                    label: this.resolveTranslation(PopupManager.CASINO_ERROR_BUTTON),
                    callback: this.onReloadGamePageClick,
                },
            ],
        })
    }

    private onSessionTimeout = async () => {
        await this.ensureTranslationsLoaded()

        this.showPopup('sessionTimeout', {
            title: this.resolveTranslation(PopupManager.CASINO_ERROR_TITLE),
            text: this.resolveTranslation('Session Time Out. Please reload the game.'),
            buttons: [
                {
                    label: this.resolveTranslation(PopupManager.CASINO_ERROR_BUTTON),
                    callback: this.onReloadGamePageClick,
                },
            ],
        })
    }

    private onHardwareAcceleration = () => {
        gsap.to(this, {
            duration: 0.05,
            onComplete: () => {
                this.showPopup('hardwareAcceleration', {
                    title: TranslationUtils.translate('hardwareAccHeader'),
                    text: TranslationUtils.translate('hardwareAccText'),
                    buttons: [
                        {
                            label: TranslationUtils.translate('backButton'),
                            callback: this.onQuitClick,
                        },
                        {
                            label: TranslationUtils.translate('reloadButton'),
                            callback: this.onReloadGamePageClick,
                        },
                    ],
                })
            },
        })
    }

    private onPreviousRoundPopup = () => {
        this.presentUnfinishedRoundPopup()
    }

    private onPreviousRoundPopupActive = () => {
        this.presentUnfinishedRoundPopup()
    }

    private presentUnfinishedRoundPopup(): void {
        if (this._currentType != null || !this._enabled) {
            return
        }

        this._currentType = 'previousRound'
        Facade.isPopupBlockingInput = true
        ;(GameSignals as any).unfinishedRoundRequested?.emit()
    }

    private onUnfinishedRoundContinue = () => {
        this.onResumeGameClick(() => undefined)
    }

    private onRealityCheckPopup = (data: any) => {
        if (data.message == null || data.message === '' || data.message === '.') {
            // manually switching to common error, when empty message provided
            void this.onCommonError()
            return
        }

        let buttons = [
            {
                label: TranslationUtils.translate('reloadButton'),
                callback: this.onReloadGamePageClick,
            },
        ]

        if (data.buttons != null) {
            buttons = data.buttons.map((button: any) => {
                return {
                    label: this.resolveTranslation(String(button.text || '')),
                    callback: this.onActionButtonClick.bind(this, button.action),
                }
            })
        }

        this.showPopup('911', {
            title: this.resolveTranslation(String(data.title || PopupManager.CASINO_ERROR_TITLE)),
            text: this.resolveTranslation(String(data.message || PopupManager.CASINO_ERROR_TEXT)),
            buttons,
        })
    }

    private onRealityCheckPopup2 = () => {
        this.showPopup('realityCheck', {
            title: TranslationUtils.translate('realityCheckHeader'),
            text: this.getRealityCheckText(),
            buttons: [
                {
                    label: TranslationUtils.translate('quitButton'),
                    callback: this.onQuitClick,
                },
                {
                    label: TranslationUtils.translate('historyButton'),
                    callback: (closePopup: () => void) => {
                        this.hidePopup()
                        closePopup()
                        window.open('', '_blank')
                    },
                },
                {
                    label: TranslationUtils.translate('keepPlayButton'),
                    callback: this.onResumeGameClick,
                },
            ],
        })
    }

    private onResumeGameClick = (closePopup: () => void) => {
        if (this._currentType === 'previousRound') {
            const okButton = TranslationUtils.translate('okButton')
            GameSignals.buttonClicked.emit(`ui-dialog-btn_${okButton}`)
        }

        this._clearAutoRestoreConfirm()
        this.hidePopup()
        closePopup()
    }

    public resetPromoFreeSpinsPopupShown() {
        this._promoFreeSpinsPopupShown = false
    }

    private onReloadGamePageClick = () => {
        location.reload()
    }

    private onQuitClick = () => {
        Utils.goToLobby()
    }

    private getRealityCheckText(): string {
        const realityCheckData = this._model.realityCheckData

        const totalSessionTime = Date.now() - realityCheckData.startSessionTime
        const hours = Math.floor(totalSessionTime / (1000 * 60 * 60))
        const minutes = Math.ceil(totalSessionTime / (1000 * 60)) - hours * 60

        return TranslationUtils.translate('realityCheckText')
            .replace('%h', hours.toString())
            .replace('%m', minutes.toString())
            .replace('%b', toMoney(realityCheckData.totalSessionBet, 2))
            .replace('%w', toMoney(realityCheckData.totalSessionWin, 2))
    }

    private getPreviousRoundText(): string {
        return ''
    }

    /**
     * `sourceText` is the English copy, which is also the dictionary key. TranslationsModule
     * normalises keys (case, collapsed whitespace, stripped punctuation) before matching, so
     * 'Would you like to enable Fast Play mode?' still resolves the
     * "Would you like to enable\nFast Play Mode?" entry the `ml` files ship.
     */
    /**
     * The init request is fired from the ServerRequests constructor, long before the loading sequence
     * reaches the point where the game loads the `ml` dictionaries. An init-time rejection (bad
     * username, dead session) would therefore be rendered with the English keys, so the dictionary
     * for the active locale is pulled in before the dialog copy is resolved. `preloadAssets` picks
     * the locale itself and returns immediately once a dictionary is in memory.
     */
    private ensureTranslationsLoaded(): Promise<void> {
        if (this._translationsReady) {
            return this._translationsReady
        }

        const translationsModule = ModulesManager.getModule('translations') as TranslationsModule | null

        if (typeof translationsModule?.preloadAssets !== 'function') {
            return Promise.resolve()
        }

        this._translationsReady = translationsModule.preloadAssets().catch((error: unknown) => {
            // Keep it retryable: a transient dictionary fetch failure must not pin every later popup
            // to the English fallback.
            this._translationsReady = null
            console.warn('[PopupManager] could not load translations for popup copy', error)
        })

        return this._translationsReady
    }

    private resolveTranslation(sourceText: string, fallback: string = sourceText): string {
        const translated = TranslationUtils.translate(sourceText)

        if (translated == null || translated.trim() === '' || translated === sourceText) {
            return fallback
        }

        return translated
    }

    /**
     * RGS errors are stable by code, while their English message may vary. Resolve the code to the
     * canonical English key stored in `public/ml/*.js`; for unmapped errors, use the server message
     * only when that exact text has a translation, otherwise show the localized generic error.
     */
    private resolveCasinoErrorText(data: any, statusCode: number): string {
        const errorCode = String(data?.error_code || data?.error?.code || data?.code || data?.error?.name || '').trim()
        const serverMessage = String(
            data?.error_message || data?.message || data?.error?.message || data?.error?.msg || ''
        ).trim()
        const normalizedErrorCode = errorCode.toUpperCase()
        const codeTranslationKey =
            ERROR_CODE_TRANSLATION_KEYS[normalizedErrorCode] ||
            (normalizedErrorCode.includes('MULTISESSION') || normalizedErrorCode.includes('MULTI_SESSION')
                ? 'Invalid session or multisession not allowed'
                : undefined)
        const messageTranslationKey = ERROR_MESSAGE_TRANSLATION_KEYS[this.normalizeErrorMessage(serverMessage)]
        const translationKey = codeTranslationKey || messageTranslationKey

        if (translationKey) {
            return this.resolveTranslation(translationKey)
        }

        if (serverMessage) {
            const translated = TranslationUtils.translate(serverMessage)
            if (translated != null && translated.trim() !== '' && translated !== serverMessage) {
                return translated
            }
        }

        if (statusCode === 804) {
            return this.resolveTranslation('Session Time Out. Please reload the game.')
        }

        return this.resolveTranslation(PopupManager.CASINO_ERROR_TEXT)
    }

    private normalizeErrorMessage(message: string): string {
        return message
            .trim()
            .toLowerCase()
            .replace(/[^\p{L}\p{N}]+/gu, ' ')
            .replace(/\s+/g, ' ')
            .trim()
    }

    private isOffline(): boolean {
        return typeof navigator !== 'undefined' && navigator.onLine === false
    }

    private isInsufficientBalanceError(data: any): boolean {
        const codes = [data?.error_code, data?.error?.code, data?.code, data?.error?.name]
            .filter((value) => value != null)
            .map((value) => String(value).toUpperCase())

        if (
            codes.includes(PopupManager.INSUFFICIENT_BALANCE_CODE) ||
            codes.some((code) => ERROR_CODE_TRANSLATION_KEYS[code] === 'You have insufficient balance!')
        ) {
            return true
        }

        const message = String(
            data?.message || data?.error_message || data?.error?.message || data?.error?.msg || ''
        ).toLowerCase()

        return message.includes('insufficient balance') || message.includes('insufficient funds')
    }

    private isPromoFreeSpinsError(data: any): boolean {
        const codes = [data?.error_code, data?.error?.code, data?.code, data?.error?.name]
            .filter((value) => value != null)
            .map((value) => String(value).toUpperCase())

        const promoCodes = PopupManager.PROMO_FREE_SPINS_CODES || []

        return codes.some((code) => promoCodes.includes(code))
    }

    private showPopup(type: string, content: PopupContent) {
        if (this._currentType != null || !this._enabled) {
            return
        }

        Facade.isPopupBlockingInput = true
        UIAPI.emit(UIGameEvent.show_dialog, content)
        this._currentType = type

        // Informational overlays must not abort the active round.
        // gamePause → IDLE + stopAutoplay, which breaks Auto Spins / Free Spins behind the swipe popup.
        if (type === 'previousRound' || type === 'swipeFeature') {
            return
        }

        if (this._model.spinData && this._model.spinData.freebet && !this._model.spinData.freebet.amount_left) {
            RoundRestoreUtil.stopRound()
        }
        GameSignals.gamePause.emit()
    }

    private hidePopup() {
        this._clearAutoRestoreConfirm()
        // Buy Bonus uses hidePopup too but owns isBuyBonusPopupOpen — don't clear Space block early.
        if (!(Facade as any).isBuyBonusPopupOpen && !this._model?.isBuyBonusPending) {
            Facade.isPopupBlockingInput = false
        }
        // Only resume when this popup actually paused the game.
        if (this._currentType !== 'previousRound' && this._currentType !== 'swipeFeature') {
            GameSignals.gameResume.emit()
        }
        this._currentType = null
        this.scheduleFastPlayPopupFlush()
    }

    private _clearAutoRestoreConfirm(): void {
        if (this._autoRestoreConfirmTimeoutId !== null) {
            clearTimeout(this._autoRestoreConfirmTimeoutId)
            this._autoRestoreConfirmTimeoutId = null
        }
    }
}
